import { keyBy, uniqBy } from 'lodash';
import moment from 'moment';
import type { Team, User } from 'api/models';
import {
  AllocationMedium,
  AllocationSourceType,
  ResourceType,
} from 'api/resource-management/types';
import { TaskEffortSetting } from 'api/tasks';
import type {
  ProjectTemplateProjectPhase,
  ProjectTemplateTask,
  TemplateAllocation,
} from 'data/templates/projects';
import { floatUid } from 'utils/uid';
import { hoursPerWeekModifier } from '../managers/hours-per-week-modifier';
import { TemplateAssignee } from '../types';
import { asAllocationEvent } from './as-allocation-event';
import { asAllocationPayloadFromEvent } from './as-allocation-payload-from-event';
import { asTemplateAllocation } from './as-template-allocation';
import { filterUncreatedTasks } from './assignee-data';
import { mergeAllocationsForAssignee } from './merge-allocations';
import { AllWorkingDayConfig } from './working-days';

export const autoAllocateFromTasks = (
  tasks: ProjectTemplateTask[],
  isHoursPerWeek?: boolean,
): TemplateAllocation[] =>
  tasks.reduce(
    (
      allocations,
      {
        effort,
        assignee,
        startDay,
        duration,
        taskId,
        taskEffortMeta,
        taskEffortSetting,
      },
    ) => {
      const assigneeCount =
        (assignee?.teams ?? []).filter((x) => x.teamType !== 'EXTERNAL')
          .length + (assignee?.users ?? []).length;

      if (
        effort == null ||
        effort === 0 ||
        assignee == null ||
        assigneeCount === 0
      ) {
        return allocations;
      }

      // if (isHoursPerWeek) {
      //   if (startDay % 5 !== 1) {
      //     do {
      //       startDay -= 1;
      //       duration += 1;
      //     } while (startDay % 5 !== 1);
      //   }

      //   if (duration % 5 !== 0) {
      //     do {
      //       duration += 1;
      //     } while (duration % 5 !== 0);
      //   }
      // }
      const effortTeamMap = (taskEffortMeta ?? []).reduce((acc, curr) => {
        if (curr.teamId) {
          if (
            taskEffortSetting === TaskEffortSetting.Equal ||
            !taskEffortSetting
          ) {
            acc[curr.teamId] = parseFloat((effort / assigneeCount).toFixed(6));
          } else {
            acc[curr.teamId] = curr.effort;
          }
        }

        return acc;
      }, {});

      const effortUserMap = (taskEffortMeta ?? []).reduce((acc, curr) => {
        if (curr.userId) {
          if (
            taskEffortSetting === TaskEffortSetting.Equal ||
            !taskEffortSetting
          ) {
            acc[curr.userId] = parseFloat((effort / assigneeCount).toFixed(6));
          } else {
            acc[curr.userId] = curr.effort;
          }
        }

        return acc;
      }, {});

      const getCommonProps = (
        taskEffSetting: TaskEffortSetting | undefined,
        dict: Record<number, number>,
        key: number,
        effortMeta: typeof taskEffortMeta,
      ) => {
        const meta = (effortMeta ?? []).find(
          (m) => m.teamId === key || m.userId === key,
        );
        const noOfWeeks = duration / 7;
        const durationToReduce =
          ((!meta?.includeSaturday ? 1 : 0) + (!meta?.includeSunday ? 1 : 0)) *
          noOfWeeks;
        const resolvedDuration = !isHoursPerWeek
          ? duration
          : duration - durationToReduce;

        const seconds = Math.floor(
          Number(
            (
              (taskEffSetting === TaskEffortSetting.Equal || !taskEffSetting
                ? parseFloat((effort / assigneeCount).toFixed(6))
                : dict[key] ?? 0) / resolvedDuration
            ).toFixed(4),
          ) * 60,
        );
        const minutes = seconds / 60;

        const commonProps = {
          startDay,
          duration,
          minutes,
          billable: true,
          medium: AllocationMedium.Hard,
          includeSaturday: meta?.includeSaturday ?? false,
          includeSunday: meta?.includeSunday ?? false,
          sources: [
            {
              sourceType: AllocationSourceType.Task,
              sourceId: taskId,
            },
          ],
        };

        return commonProps;
      };

      const processedAllocations = allocations
        .concat(
          assignee.teams
            .filter((x) => x.teamType !== 'EXTERNAL')
            .map(
              (x) =>
                ({
                  id: floatUid(),
                  ...getCommonProps(
                    taskEffortSetting,
                    effortTeamMap,
                    x.teamId,
                    taskEffortMeta,
                  ),
                  type: ResourceType.Team,
                  teamId: x.teamId,
                } as TemplateAllocation),
            ),
        )
        .concat(
          assignee.users.map(
            (x) =>
              ({
                id: floatUid(),
                ...getCommonProps(
                  taskEffortSetting,
                  effortUserMap,
                  x.userId,
                  taskEffortMeta,
                ),
                type: ResourceType.User,
                userId: x.userId,
              } as TemplateAllocation),
          ),
        );

      if (isHoursPerWeek) {
        return hoursPerWeekModifier(processedAllocations);
      }

      return processedAllocations;
    },
    [] as TemplateAllocation[],
  );

export const getAutoAllocatedAssignees = (
  allTasks: ProjectTemplateTask[],
): TemplateAssignee =>
  filterUncreatedTasks(allTasks).reduce(
    (assignees, task) => {
      const { effort, assignee } = task;
      const assigneeCount =
        (assignee?.teams ?? []).length + (assignee?.users ?? []).length;

      if (
        effort == null ||
        effort === 0 ||
        assignee == null ||
        assigneeCount === 0
      ) {
        return assignees;
      }

      return {
        users: uniqBy(
          assignees.users.concat(task.assignee?.users ?? []),
          'userId',
        ),
        teams: uniqBy(
          assignees.teams.concat(task.assignee?.teams ?? []),
          'teamId',
        ),
      };
    },
    { users: [], teams: [] } as TemplateAssignee,
  );

export const getAutoAllocatedAllocationsFromTasks = (
  tasks: ProjectTemplateTask[],
): TemplateAllocation[] => {
  const rawAllocations = autoAllocateFromTasks(tasks);
  const allocatedMembers = getAutoAllocatedAssignees(tasks);
  const userLookup = keyBy(allocatedMembers.users, 'userId');
  const teamLookup = keyBy(allocatedMembers.teams, 'teamId');
  const relativeDate = moment().startOf('day');
  const allocationEvents = asAllocationEvent({
    relativeDate,
    userLookup,
    teamLookup,
    projectId: floatUid(),
    projectPhaseLookup: {},
    workingDaysConfig: AllWorkingDayConfig,
  })(rawAllocations);

  return mergeAllocationsForAssignee(
    allocatedMembers,
    AllWorkingDayConfig,
  )(allocationEvents).map((x) =>
    asTemplateAllocation(relativeDate)(asAllocationPayloadFromEvent(x)),
  );
};

export const autoAllocateFromPhases = (
  phases: ProjectTemplateProjectPhase[],
  isHoursPerWeek?: boolean,
) =>
  phases.reduce(
    (allocations, { assignees, projectPhaseId, startDay, duration }) => {
      if (assignees == null || assignees.length === 0) {
        return allocations;
      }

      assignees.forEach((assignee) => {
        if (assignee.effort == null || assignee.effort === 0) {
          return;
        }
        const noOfWeeks = duration / 7;
        const durationToReduce =
          ((!assignee.includeSaturday ? 1 : 0) +
            (!assignee.includeSunday ? 1 : 0)) *
          noOfWeeks;
        const resolvedDuration = !isHoursPerWeek
          ? duration
          : duration - durationToReduce;
        const seconds = Math.floor(
          Number(assignee.effort / resolvedDuration) * 60,
        );
        const minutes = seconds / 60;
        const commonProps = {
          startDay,
          duration,
          minutes,
          billable: true,
          medium: AllocationMedium.Hard,
          includeSaturday: assignee.includeSaturday ?? false,
          includeSunday: assignee.includeSunday ?? false,
          sources: [
            {
              sourceType: AllocationSourceType.Phase,
              sourceId: projectPhaseId,
            },
          ],
        };

        allocations.push({
          id: floatUid(),
          ...commonProps,
          type: assignee.user ? ResourceType.User : ResourceType.Team,
          userId: assignee.user?.userId,
          teamId: assignee.team?.teamId,
        } as TemplateAllocation);
      });

      if (isHoursPerWeek) {
        return hoursPerWeekModifier(allocations);
      }

      return allocations;
    },
    [] as TemplateAllocation[],
  );

export const getAutoAllocatedAssigneesFromPhases = (
  phases: ProjectTemplateProjectPhase[],
): TemplateAssignee => {
  return phases.reduce(
    (allAssignees, phase) => {
      if (phase.isNew) {
        return allAssignees;
      }

      return {
        users: uniqBy(
          allAssignees.users.concat(
            phase.assignees?.reduce<User[]>((acc, x) => {
              if (x.user?.userId != null && x.effort != null && x.effort > 0) {
                acc.push(x.user as User);
              }

              return acc;
            }, []) ?? [],
          ),
          'userId',
        ),
        teams: uniqBy(
          allAssignees.teams.concat(
            phase.assignees?.reduce<Team[]>((acc, x) => {
              if (x.team?.teamId != null && x.effort != null && x.effort > 0) {
                acc.push(x.team as Team);
              }

              return acc;
            }, []) ?? [],
          ),
          'teamId',
        ),
      };
    },
    { users: [], teams: [] } as TemplateAssignee,
  );
};

export const getAutoAllocatedAllocationsFromPhases = (
  phases: ProjectTemplateProjectPhase[],
): TemplateAllocation[] => {
  const rawAllocations = autoAllocateFromPhases(phases);
  const allocatedMembers = getAutoAllocatedAssigneesFromPhases(phases);
  const userLookup = keyBy(allocatedMembers.users, 'userId');
  const teamLookup = keyBy(allocatedMembers.teams, 'teamId');
  const relativeDate = moment().startOf('day');
  const allocationEvents = asAllocationEvent({
    relativeDate,
    userLookup,
    teamLookup,
    projectId: floatUid(),
    projectPhaseLookup: {},
    workingDaysConfig: AllWorkingDayConfig,
  })(rawAllocations);

  return mergeAllocationsForAssignee(
    allocatedMembers,
    AllWorkingDayConfig,
  )(allocationEvents).map((x) =>
    asTemplateAllocation(relativeDate)(asAllocationPayloadFromEvent(x)),
  );
};
